/* eslint-disable */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './Employee.css';

/**
 * UrgencyOverviewModal
 * Premium Apple-inspired modal for certificate urgency (Dringlichkeits-Zentrale)
 */
export default function UrgencyOverviewModal({
    isOpen,
    onClose,
    employees = [],
    documents = [],
    documentTypes = [],
    onEditDoc,
    onSelectEmployee
}) {
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    const [activeTab, setActiveTab] = useState('employees'); // 'employees' | 'list'
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(['expired', 'warning']); // Default: show both
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [showStatusFilter, setShowStatusFilter] = useState(false);

    const statusFilterRef = useRef(null);

    // --- UTILS ---
    const getDocStatus = (doc) => {
        if (!doc.expiry_date) return { type: 'valid', label: 'Gültig', color: '#34c759' };
        const now = new Date();
        const expiry = new Date(doc.expiry_date);
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        const warningDays = doc.warning_days_override ?? doc.warning_days_default ?? 60;

        if (diffDays < 0) return { type: 'expired', label: 'Abgelaufen', color: '#ff3b30', days: diffDays };
        if (diffDays <= warningDays) return { type: 'warning', label: `Läuft ab in ${diffDays} Tagen`, color: '#ff9500', days: diffDays };
        return { type: 'valid', label: 'Gültig', color: '#34c759', days: diffDays };
    };

    const getAvatarColor = (name) => {
        const colors = ['#007aff', '#5856d6', '#af52de', '#ff2d55', '#ff9500', '#34c759', '#5ac8fa', '#ff6482', '#30b0c7'];
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return colors[Math.abs(hash) % colors.length];
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // --- DATA PROCESSING ---
    const allUrgencyItems = useMemo(() => {
        return documents
            .map(d => {
                const emp = employees.find(e => e.id === d.worker_id);
                return { ...d, status: getDocStatus(d), worker_color: emp?.color };
            })
            .filter(d => d.status.type === 'expired' || d.status.type === 'warning')
            .sort((a, b) => (a.status.days || 0) - (b.status.days || 0));
    }, [documents]);

    const filteredItems = useMemo(() => {
        return allUrgencyItems.filter(item => {
            const matchesSearch = !search ||
                item.worker_name.toLowerCase().includes(search.toLowerCase()) ||
                (item.type_name || '').toLowerCase().includes(search.toLowerCase());

            const matchesStatus = statusFilter.includes(item.status.type);

            return matchesSearch && matchesStatus;
        });
    }, [allUrgencyItems, search, statusFilter]);

    const groupedByEmployee = useMemo(() => {
        const groups = {};
        filteredItems.forEach(item => {
            if (!groups[item.worker_id]) {
                const emp = employees.find(e => e.id === item.worker_id) || { name: item.worker_name, id: item.worker_id };
                groups[item.worker_id] = {
                    id: item.worker_id,
                    name: emp.name,
                    color: emp.color,
                    category: emp.category_name || 'Keine Rolle',
                    expired: 0,
                    warning: 0,
                    items: []
                };
            }
            groups[item.worker_id].items.push(item);
            if (item.status.type === 'expired') groups[item.worker_id].expired++;
            else groups[item.worker_id].warning++;
        });
        return Object.values(groups).sort((a, b) => (b.expired * 100 + b.warning) - (a.expired * 100 + a.warning));
    }, [filteredItems, employees]);

    // --- HANDLERS ---
    const toggleExpand = (id) => {
        const next = new Set(expandedEmployees);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setExpandedEmployees(next);
    };

    const toggleStatus = (s) => {
        if (statusFilter.includes(s)) {
            setStatusFilter(statusFilter.filter(item => item !== s));
        } else {
            setStatusFilter([...statusFilter, s]);
        }
    };

    // Close click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    return (
        <div className="uo-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="uo-modal-container" onClick={e => e.stopPropagation()}>

                <div className="uo-header">
                    <div className="uo-title-area">
                        <h2>
                            🚨 Nachweis-Zentrale
                            <span className="uo-badge-count">{filteredItems.length}</span>
                        </h2>
                        <p className="uo-subtitle">Abgelaufene und bald ablaufende Nachweise aller Mitarbeiter im Überblick</p>
                    </div>
                    <button className="uo-close-btn" onClick={onClose}>✕</button>
                </div>

                {/* Tabs */}
                <div className="uo-tabs">
                    <div
                        className={`uo-tab ${activeTab === 'employees' ? 'active' : ''}`}
                        onClick={() => setActiveTab('employees')}
                    >
                        👥 Mitarbeiter
                    </div>
                    <div
                        className={`uo-tab ${activeTab === 'list' ? 'active' : ''}`}
                        onClick={() => setActiveTab('list')}
                    >
                        🔥 Handlungsbedarf
                    </div>
                </div>

                {/* Filter Bar */}
                <div className="uo-filter-bar">
                    <div className="uo-search-wrapper">
                        <span className="uo-search-icon">🔍</span>
                        <input
                            type="text"
                            className="uo-search-input"
                            placeholder="Suche"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="uo-filter-group" ref={statusFilterRef} style={{ position: 'relative' }}>
                        <label className="uo-filter-label">Status</label>
                        <button
                            className="uo-select"
                            onClick={() => setShowStatusFilter(!showStatusFilter)}
                        >
                            <span>
                                {statusFilter.length === 2 ? 'Alle Status' :
                                    statusFilter.length === 0 ? 'Kein Status' :
                                        statusFilter[0] === 'expired' ? 'Abgelaufen' : 'Warnung'}
                            </span>
                            <span style={{ fontSize: '0.6rem', marginLeft: 8 }}>▼</span>
                        </button>
                        {showStatusFilter && (
                            <div className="uo-filter-dropdown-menu">
                                <div className="uo-filter-dropdown-list">
                                    <div className="uo-filter-item" onClick={() => toggleStatus('expired')}>
                                        <div className={`uo-checkbox ${statusFilter.includes('expired') ? 'checked' : ''}`}>
                                            {statusFilter.includes('expired') && <span className="uo-checkmark">✓</span>}
                                        </div>
                                        <span className="uo-dot" style={{ background: '#ff3b30' }}></span>
                                        <span className="uo-label">Abgelaufen</span>
                                    </div>
                                    <div className="uo-filter-item" onClick={() => toggleStatus('warning')}>
                                        <div className={`uo-checkbox ${statusFilter.includes('warning') ? 'checked' : ''}`}>
                                            {statusFilter.includes('warning') && <span className="uo-checkmark">✓</span>}
                                        </div>
                                        <span className="uo-dot" style={{ background: '#ff9500' }}></span>
                                        <span className="uo-label">Warnung</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {activeTab === 'employees' && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignSelf: 'flex-end', marginBottom: '4px' }}>
                            <button className="uo-expand-btn" onClick={() => setExpandedEmployees(new Set(groupedByEmployee.map(g => g.id)))}>Alle auf</button>
                            <button className="uo-expand-btn" onClick={() => setExpandedEmployees(new Set())}>Alle zu</button>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="uo-content">
                    {filteredItems.length === 0 ? (
                        <div className="uo-empty-state">
                            <div className="uo-empty-icon">✅</div>
                            <h3 className="uo-empty-title">Alles in Ordnung</h3>
                            <p className="uo-empty-text">Keine kritischen Nachweise mit den aktuellen Filtern gefunden.</p>
                        </div>
                    ) : activeTab === 'employees' ? (
                        groupedByEmployee.map(group => {
                            const isExpanded = expandedEmployees.has(group.id) || groupedByEmployee.length === 1;
                            return (
                                <div key={group.id} className="uo-resource-group">
                                    <div className="uo-resource-header" onClick={() => toggleExpand(group.id)}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div className="uo-avatar" style={{ background: group.color || getAvatarColor(group.name) }}>
                                                {getInitials(group.name)}
                                            </div>
                                            <div className="uo-resource-info">
                                                <h3>{group.name} <span className="uo-res-role">| {group.category}</span></h3>
                                                <div className="uo-resource-stats">
                                                    {group.expired > 0 && <span className="uo-stat-pill expired">{group.expired} abgelaufen</span>}
                                                    {group.warning > 0 && <span className="uo-stat-pill warning">{group.warning} bald fällig</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ marginLeft: 12, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                                ▼
                                            </span>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="uo-conflict-list">
                                            {group.items.map((item, idx) => (
                                                <div key={idx} className="uo-conflict-item">
                                                    <div className="uo-item-type">
                                                        <span className="uo-type-name">{item.type_name}</span>
                                                        <span className={`uo-status-badge ${item.status.type}`}>
                                                            {item.status.label}
                                                        </span>
                                                    </div>
                                                    <div className="uo-item-date">
                                                        <span>Ablauf:</span> {formatDate(item.expiry_date)}
                                                    </div>
                                                    <div className="uo-item-actions">
                                                        <button
                                                            className="uo-edit-icon-btn"
                                                            onClick={(e) => { e.stopPropagation(); onEditDoc(item); }}
                                                            title="Bearbeiten"
                                                        >
                                                            ✏️
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        /* Flat List */
                        <div className="uo-flat-list">
                            <div className="uo-list-header">
                                <div className="uo-header-cell uo-col-resource">Mitarbeiter</div>
                                <div className="uo-header-cell uo-col-type">Nachweisart</div>
                                <div className="uo-header-cell uo-col-date">Datum</div>
                                <div className="uo-header-cell uo-col-status">Status</div>
                                <div className="uo-header-cell uo-col-action">Aktion</div>
                            </div>

                            <div className="uo-list-rows">
                                {filteredItems.map((item, idx) => (
                                    <div key={idx} className="uo-conflict-row">
                                        <div className="uo-col-resource">
                                            <div className="uo-avatar-mini" style={{ background: item.worker_color || getAvatarColor(item.worker_name) }}>
                                                {getInitials(item.worker_name)}
                                            </div>
                                            <span className="uo-res-name">{item.worker_name}</span>
                                        </div>

                                        <div className="uo-col-type">
                                            {item.type_name}
                                        </div>

                                        <div className="uo-col-date">
                                            {formatDate(item.expiry_date)}
                                        </div>

                                        <div className="uo-col-status">
                                            <span className={`uo-status-badge ${item.status.type}`}>
                                                {item.status.label}
                                            </span>
                                        </div>

                                        <div className="uo-col-action">
                                            <button
                                                className="uo-edit-icon-btn"
                                                onClick={() => onEditDoc(item)}
                                                title="Bearbeiten"
                                            >
                                                ✏️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="uo-footer">
                    <button className="uo-btn-primary" onClick={onClose}>
                        Fertig
                    </button>
                </div>
            </div>
        </div>
    );
}
